import { useEffect, useId } from "react";
import { useModalStack } from "@/providers/ModalStackProvider";

/**
 * Hook to handle Escape key press when a condition is met.
 * Integrates with ModalStackProvider to only respond when this is the topmost modal.
 * @param isActive - Whether the escape key handler should be active
 * @param onEscape - Callback to execute when Escape is pressed
 */
export function useEscapeKey(isActive: boolean, onEscape: () => void) {
  const modalId = useId();
  const { registerModal, unregisterModal, isTopmost } = useModalStack();

  // Register with modal stack when active
  useEffect(() => {
    if (isActive) {
      const previousElement = document.activeElement as HTMLElement;
      registerModal(modalId, previousElement);
      return () => {
        unregisterModal(modalId);
      };
    }
  }, [isActive, modalId, registerModal, unregisterModal]);

  // Escape key handler - only for topmost modal
  useEffect(() => {
    if (!isActive || !isTopmost(modalId)) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onEscape();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isActive, isTopmost, modalId, onEscape]);
}
